import type React from "react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalTitle,
} from "@/components/ui/modal";
import { Textarea } from "@/components/ui/textarea";
import { FormCreationWizard } from "../../form-creation-wizard";
import { FormSettingsModal } from "../../form-settings-modal";
import { JsonViewModal } from "../../modals/json-view-modal";
import { ShareFormModal } from "../../modals/share-form-modal";

import type { FormBuilderModalsProps } from "../types";

export const FormBuilderModals: React.FC<FormBuilderModalsProps> = ({
	showSettings,
	showFormSettings,
	showJsonView,
	showCreationWizard,
	showShareModal,
	formSchema,
	formId,
	formSlug,
	isPublished,
	onCloseSettings,
	onCloseFormSettings,
	onCloseJsonView,
	onCloseCreationWizard,
	onCloseShareModal,
	onFormTypeSelect,
	onFormSettingsUpdate,
	onSchemaUpdate,
	onPublish,
	userEmail,
}) => {
	const t = useTranslations("builder");
	return (
	<>
		{}
		<Modal onOpenChange={onCloseSettings} open={showSettings}>
			<ModalContent className="flex flex-col gap-6 bg-card text-card-foreground max-sm:p-4">
				<ModalHeader>
					<ModalTitle>{t("settingsModal.title")}</ModalTitle>
				</ModalHeader>
				<div className="flex flex-col gap-6">
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-1">
							<Label htmlFor="form-title">{t("settingsModal.internalTitle")}</Label>
							<Input
								id="form-title"
								onChange={(e) =>
									onFormSettingsUpdate({ title: e.target.value })
								}
								placeholder={t("settingsModal.internalTitlePlaceholder")}
								value={formSchema.settings?.title || ""}
							/>
							<p className="text-muted-foreground text-xs">
								{t("settingsModal.internalTitleHint")}
							</p>
						</div>

						<div className="flex flex-col gap-1">
							<Label htmlFor="form-public-title">{t("settingsModal.publicTitle")}</Label>
							<Input
								id="form-public-title"
								onChange={(e) =>
									onFormSettingsUpdate({ publicTitle: e.target.value })
								}
								placeholder={t("settingsModal.publicTitlePlaceholder")}
								value={formSchema.settings?.publicTitle || ""}
							/>
							<p className="text-muted-foreground text-xs">
								{t("settingsModal.publicTitleHint")}
							</p>
						</div>

						<div className="flex flex-col gap-1">
							<Label htmlFor="form-description">{t("settingsModal.description")}</Label>
							<Textarea
								id="form-description"
								onChange={(e) =>
									onFormSettingsUpdate({ description: e.target.value })
								}
								placeholder={t("settingsModal.descriptionPlaceholder")}
								rows={3}
								value={formSchema.settings?.description || ""}
							/>
						</div>

						<div className="flex flex-col gap-1">
							<Label htmlFor="submit-text">{t("settingsModal.submitText")}</Label>
							<Input
								id="submit-text"
								onChange={(e) =>
									onFormSettingsUpdate({ submitText: e.target.value })
								}
								placeholder={t("settingsModal.submitTextPlaceholder")}
								value={formSchema.settings?.submitText || t("settingsModal.submitDefault")}
							/>
						</div>

						<div className="flex flex-col gap-1">
							<Label htmlFor="success-message">{t("settingsModal.successMessage")}</Label>
							<Textarea
								id="success-message"
								onChange={(e) =>
									onFormSettingsUpdate({ successMessage: e.target.value })
								}
								placeholder={t("settingsModal.successMessagePlaceholder")}
								rows={2}
								value={formSchema.settings?.successMessage || ""}
							/>
						</div>

						<div className="flex flex-col gap-1">
							<Label htmlFor="redirect-url">{t("settingsModal.redirectUrl")}</Label>
							<Input
								id="redirect-url"
								onChange={(e) =>
									onFormSettingsUpdate({ redirectUrl: e.target.value })
								}
								placeholder="https://example.com/thank-you"
								value={formSchema.settings?.redirectUrl || ""}
							/>
						</div>
					</div>

					<div className="flex justify-end gap-2">
						<Button onClick={onCloseSettings} variant="secondary">
							{t("cancel")}
						</Button>
						<Button onClick={onCloseSettings}>{t("settingsModal.save")}</Button>
					</div>
				</div>
			</ModalContent>
		</Modal>

		{}
		<FormCreationWizard
			isOpen={showCreationWizard}
			onClose={onCloseCreationWizard}
			onFormTypeSelect={onFormTypeSelect}
		/>

		{}
		<JsonViewModal
			isOpen={showJsonView}
			onClose={onCloseJsonView}
			schema={formSchema}
		/>

		{}
		<FormSettingsModal
			formId={formId}
			isOpen={showFormSettings}
			onClose={onCloseFormSettings}
			onSchemaUpdate={onSchemaUpdate}
			schema={formSchema}
			userEmail={userEmail}
		/>

		{}
		<ShareFormModal
			formId={formId ?? null}
			formSlug={formSlug ?? null}
			isOpen={showShareModal}
			isPublished={isPublished}
			onClose={onCloseShareModal}
			onPublish={onPublish}
		/>
	</>
	);
};
